import { useState, useEffect } from "react";
import Swal from "sweetalert2";

export interface AccountJob {
  invoice_code: string;
  status: string;
  email: string;
  no_wa: string;
  harga: number;
  ktp_image_url: string;
  bukti_bayar_url: string | null;
  bukti_bayar_uploaded_at: string | null;
  preview_data: {
    nik?: string;
    namaLengkap?: string;
    ttl?: string;
    jenisKelamin?: string;
    desa?: string;
    kota?: string;
    provinsi?: string;
    kodepos?: string;
  } | null;
  phone_created: string | null;
  error_message: string | null;
  created_at: string;
}

const PREVIEW_FIELDS: Array<{ key: keyof NonNullable<AccountJob["preview_data"]>; label: string }> = [
  { key: "nik", label: "NIK" },
  { key: "namaLengkap", label: "Nama Lengkap" },
  { key: "ttl", label: "TTL" },
  { key: "jenisKelamin", label: "Jenis Kelamin" },
  { key: "desa", label: "Desa" },
  { key: "kota", label: "Kota" },
  { key: "provinsi", label: "Provinsi" },
  { key: "kodepos", label: "Kode Pos" },
];

function statusBadge(status: string) {
  const map: Record<string, { cls: string; label: string }> = {
    "BELUM BAYAR": { cls: "badge-pending", label: "Belum Bayar" },
    "MENUNGGU PEMBAYARAN": { cls: "badge-pending", label: "Menunggu Verifikasi" },
    LUNAS: { cls: "badge-success", label: "Lunas" },
    PREVIEW: { cls: "badge-pending", label: "Preview OCR" },
    PROSES: { cls: "badge-pending", label: "Proses Bot" },
    SELESAI: { cls: "badge-success", label: "Selesai" },
    GAGAL: { cls: "badge-danger", label: "Gagal" },
    DIBATALKAN: { cls: "badge-danger", label: "Dibatalkan" },
  };
  return map[status] || { cls: "badge-pending", label: status };
}

async function fetchPending(): Promise<AccountJob[]> {
  const res = await fetch(`/api/accounts/admin/pending`);
  const data = await res.json();
  if (data.success) return data.pending || [];
  throw new Error(data.error || "Gagal mengambil data");
}

async function postJson(path: string, body: unknown) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function AccountsPanel() {
  const [jobs, setJobs] = useState<AccountJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AccountJob | null>(null);
  const [showKtp, setShowKtp] = useState<string | null>(null);
  const [previewForm, setPreviewForm] = useState<Record<string, string>>({});

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setJobs(await fetchPending());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleVerify = async (job: AccountJob) => {
    const result = await Swal.fire({
      title: "Verifikasi Pembayaran",
      text: `Tandai ${job.invoice_code} sebagai LUNAS dan jalankan OCR preview?`,
      showCancelButton: true,
      confirmButtonText: "Ya, Verifikasi",
      cancelButtonText: "Batal",
      confirmButtonColor: "#28a745",
    });
    if (!result.isConfirmed) return;
    try {
      await postJson("/api/accounts/admin/verify", { invoice_code: job.invoice_code });
      await Swal.fire({ icon: "success", title: "Pembayaran Diverifikasi", text: "OCR preview sedang diproses bot di VM." });
      await load();
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Gagal", text: e instanceof Error ? e.message : String(e) });
    }
  };

  const openPreview = (job: AccountJob) => {
    setSelected(job);
    const p = job.preview_data || {};
    const form: Record<string, string> = {};
    for (const f of PREVIEW_FIELDS) form[f.key] = p[f.key] || "";
    setPreviewForm(form);
  };

  const handleApprove = async () => {
    if (!selected) return;
    const result = await Swal.fire({
      title: "Setujui & Buat Akun",
      text: `Jalankan bot untuk membuat akun ${selected.email}?`,
      showCancelButton: true,
      confirmButtonText: "Setujui & Buat",
      cancelButtonText: "Batal",
      confirmButtonColor: "#28a745",
    });
    if (!result.isConfirmed) return;
    try {
      await postJson("/api/accounts/admin/approve", {
        invoice_code: selected.invoice_code,
        preview: previewForm,
      });
      await Swal.fire({ icon: "success", title: "Bot Dijalankan", text: "Status akan diperbarui saat bot selesai." });
      setSelected(null);
      await load();
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Gagal", text: e instanceof Error ? e.message : String(e) });
    }
  };

  const handleReject = async (job: AccountJob) => {
    const result = await Swal.fire({
      title: "Tolak Job",
      input: "textarea",
      inputLabel: "Catatan (opsional)",
      showCancelButton: true,
      confirmButtonText: "Tolak",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      await postJson("/api/accounts/admin/reject", {
        invoice_code: job.invoice_code,
        catatan_admin: result.value || "",
      });
      await load();
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Gagal", text: e instanceof Error ? e.message : String(e) });
    }
  };

  if (loading && jobs.length === 0) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ fontSize: "18px", fontWeight: "700" }}>Job Buat Akun Prometric ({jobs.length})</h3>
        <button onClick={load} disabled={loading} className="btn btn-primary">🔄 Refresh</button>
      </div>

      {error && <div style={{ backgroundColor: "#f8d7da", color: "#721c24", padding: "12px", borderRadius: "8px", marginBottom: "16px" }}>{error}</div>}

      {jobs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>Tidak ada job buat akun</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Email</th>
                <th>No WA</th>
                <th>Nominal</th>
                <th>Status</th>
                <th>KTP</th>
                <th>Bukti</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const badge = statusBadge(job.status);
                return (
                  <tr key={job.invoice_code}>
                    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>{job.invoice_code}</td>
                    <td>{job.email}</td>
                    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>{job.no_wa}</td>
                    <td>{job.harga ? `Rp ${job.harga.toLocaleString("id-ID")}` : "-"}</td>
                    <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                    <td>
                      <button className="btn btn-primary" style={{ padding: "4px 10px", fontSize: "12px" }} onClick={() => setShowKtp(job.ktp_image_url)}>
                        Lihat
                      </button>
                    </td>
                    <td>
                      {job.bukti_bayar_url ? (
                        <a href={job.bukti_bayar_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px" }}>Lihat</a>
                      ) : (
                        <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {job.status === "MENUNGGU PEMBAYARAN" && (
                          <button className="btn btn-success" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => handleVerify(job)}>
                            Verifikasi
                          </button>
                        )}
                        {(job.status === "PREVIEW" || job.status === "LUNAS") && (
                          <button className="btn btn-success" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => openPreview(job)}>
                            Koreksi & Setujui
                          </button>
                        )}
                        {job.status === "PROSES" && <span style={{ fontSize: "12px", color: "#856404" }}>⏳ berjalan...</span>}
                        {job.status === "SELESAI" && (
                          <span style={{ fontSize: "12px", color: "#155724" }}>
                            ✅ {job.phone_created ? `HP: ${job.phone_created}` : "selesai"}
                          </span>
                        )}
                        {job.status === "GAGAL" && (
                          <span style={{ fontSize: "12px", color: "#721c24" }} title={job.error_message || ""}>❌ {job.error_message || "gagal"}</span>
                        )}
                        {["BELUM BAYAR", "MENUNGGU PEMBAYARAN", "PREVIEW", "LUNAS", "GAGAL"].includes(job.status) && (
                          <button className="btn btn-danger" style={{ padding: "5px 10px", fontSize: "12px" }} onClick={() => handleReject(job)}>
                            Tolak
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Koreksi Data & Setujui</div>
              <button className="btn-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <p style={{ fontSize: "13px", marginBottom: "16px" }}>
              <strong>{selected.invoice_code}</strong> — {selected.email}
            </p>
            <p style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
              Periksa dan perbaiki hasil OCR dari KTP sebelum bot membuat akun.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              {PREVIEW_FIELDS.map((f) => (
                <div className="form-group" key={f.key} style={{ marginBottom: "12px" }}>
                  <label className="form-label" style={{ fontSize: "12px" }}>{f.label}</label>
                  <input
                    className="form-textarea"
                    style={{ minHeight: "auto", padding: "8px 10px" }}
                    value={previewForm[f.key] || ""}
                    onChange={(e) => setPreviewForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className="btn-group">
              <button className="btn btn-danger" onClick={() => { setSelected(null); handleReject(selected); }}>Tolak</button>
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Batal</button>
              <button className="btn btn-success" onClick={handleApprove}>Setujui & Buat Akun</button>
            </div>
          </div>
        </div>
      )}

      {showKtp && (
        <div className="image-modal" onClick={() => setShowKtp(null)}>
          <img src={showKtp} alt="KTP" />
        </div>
      )}
    </div>
  );
}
